import React, { FC, useMemo, useState } from 'react';

import { Alert, Checkbox, Input, Select, Spin, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  TCrmSchoolRow,
  TCrmSchools,
  TCrmSchoolStatus,
  TCrmSignal,
} from '../../../../shared/common/api/endpoints/crmAnalyticsApi';
import { SIGNAL_META, STATUS_META, STATUS_ORDER } from '../constants';
import styles from '../CrmAnalytics.module.scss';
import { formatDate, formatDays, formatMoney, formatNumber } from '../utils';
import { HintTitle, StatusLabel } from './common';

interface Props {
  schools: TCrmSchools | null;
  loading: boolean;
  error: string | null;
  signals: TCrmSignal[];
  onSignalsChange: (signals: TCrmSignal[]) => void;
}

const STATUS_FILTER: TCrmSchoolStatus[] = [ ...STATUS_ORDER, 'disconnected' ];

const byNumber = (key: keyof TCrmSchoolRow) => (a: TCrmSchoolRow, b: TCrmSchoolRow) =>
  Number(a[key] ?? -Infinity) - Number(b[key] ?? -Infinity);

const byDate = (key: keyof TCrmSchoolRow) => (a: TCrmSchoolRow, b: TCrmSchoolRow) =>
  String(a[key] ?? '').localeCompare(String(b[key] ?? ''));

const SchoolsTab: FC<Props> = ({ schools, loading, error, signals, onSignalsChange }) => {
  const [ search, setSearch ] = useState('');
  const [ statuses, setStatuses ] = useState<TCrmSchoolStatus[]>([]);
  const [ paidOnly, setPaidOnly ] = useState(false);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (schools?.rows ?? []).filter((row) => {
      if (query && !`${row.id} ${row.name ?? ''} ${row.domain ?? ''}`.toLowerCase().includes(query)) return false;
      if (statuses.length > 0 && (!row.status || !statuses.includes(row.status))) return false;
      if (signals.length > 0 && !signals.some((signal) => row.signals.includes(signal))) return false;
      if (paidOnly && row.cash_real <= 0) return false;
      return true;
    });
  }, [ schools, search, statuses, signals, paidOnly ]);

  if (error) return <Alert type="error" showIcon message={error} />;

  const columns: ColumnsType<TCrmSchoolRow> = [
    {
      title: 'Школа',
      key: 'name',
      fixed: 'left',
      width: 240,
      render: (_, row) => (
        <div>
          <div className={styles.schoolName}>{row.name || '—'}</div>
          <div className={styles.muted}>{row.domain} · id {row.id}{row.is_excluded ? ' · системный тег' : ''}</div>
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 160,
      render: (status: TCrmSchoolStatus | null) => <StatusLabel status={status} />,
    },
    {
      title: <HintTitle title="Сигналы" hint="Что требует внимания: долг, баланс на исходе, не заходят в кабинет, перестала платить, работает без оплаты" />,
      key: 'signals',
      width: 190,
      render: (_, row) => row.signals.map((signal) => (
        <Tooltip key={signal} title={SIGNAL_META[signal].hint}>
          <Tag color={SIGNAL_META[signal].color} style={{ marginBottom: 2 }}>{SIGNAL_META[signal].label}</Tag>
        </Tooltip>
      )),
    },
    {
      title: <HintTitle title="LTV" hint="Реальные платежи школы за всё время" />,
      dataIndex: 'cash_real',
      align: 'right',
      sorter: byNumber('cash_real'),
      defaultSortOrder: 'descend',
      render: formatMoney,
    },
    {
      title: <HintTitle title="Начислено" hint="Списания за доступ с основного счёта за всё время" />,
      dataIndex: 'accrual_real',
      align: 'right',
      sorter: byNumber('accrual_real'),
      render: formatMoney,
    },
    {
      title: <HintTitle title="Комиссия партнёров" hint="Начислено партнёрам по платежам школы" />,
      dataIndex: 'partner_commission',
      align: 'right',
      sorter: byNumber('partner_commission'),
      render: formatMoney,
    },
    {
      title: <HintTitle title="Платящая жизнь" hint="Дней с оплаченным доступом за всё время" />,
      dataIndex: 'paid_days',
      align: 'right',
      sorter: byNumber('paid_days'),
      render: formatDays,
    },
    {
      title: <HintTitle title="В сервисе" hint="От подключения до последнего дня с доступом (тест, оплачен, долг, живой льготный)" />,
      dataIndex: 'service_days',
      align: 'right',
      sorter: byNumber('service_days'),
      render: formatDays,
    },
    {
      title: <HintTitle title="Периодов оплаты" hint="Сколько раз школа начинала платить: перерыв больше 30 дней — новый период" />,
      dataIndex: 'episodes',
      align: 'right',
      sorter: byNumber('episodes'),
      render: formatNumber,
    },
    {
      title: 'Подключена',
      dataIndex: 'connected_at',
      sorter: byDate('connected_at'),
      render: formatDate,
    },
    {
      title: <HintTitle title="Первая оплата" hint="Дата первого реального платежа и сколько дней прошло с подключения" />,
      key: 'first_payment',
      sorter: byDate('first_payment'),
      render: (_, row) => (row.first_payment
        ? (
          <div>
            <div>{formatDate(row.first_payment)}</div>
            <div className={styles.muted}>через {formatDays(row.days_to_pay)}</div>
          </div>
        )
        : '—'),
    },
    {
      title: <HintTitle title="Последний оплаченный день" hint="Последний день с оплаченным доступом. «Ушла» — после него 30 дней и больше без оплаты" />,
      key: 'last_paid_day',
      sorter: byDate('last_paid_day'),
      render: (_, row) => (row.last_paid_day
        ? (
          <span>
            {formatDate(row.last_paid_day)}
            {row.churned && <Tag style={{ marginLeft: 6 }}>ушла</Tag>}
          </span>
        )
        : '—'),
    },
    {
      title: <HintTitle title="Баланс" hint="Остаток основного счёта; бонусный — ниже" />,
      dataIndex: 'balance_main',
      align: 'right',
      sorter: byNumber('balance_main'),
      render: (value: number, row) => (
        <div>
          <div>{formatMoney(value)}</div>
          {row.balance_bonus > 0 && <div className={styles.muted}>бонус {formatMoney(row.balance_bonus)}</div>}
        </div>
      ),
    },
    {
      title: <HintTitle title="Осталось дней" hint="До конца оплаченного доступа — у платящих и в долгу" />,
      dataIndex: 'days_left',
      align: 'right',
      sorter: byNumber('days_left'),
      render: (value: number | null, row) => (value === null
        ? '—'
        : (
          <Tooltip title={`Доступ до ${formatDate(row.access_until)}`}>
            <span>{formatNumber(value)}</span>
          </Tooltip>
        )),
    },
    {
      title: <HintTitle title="Выручка школы за 30 дней" hint="Выручка школы в GetCourse — от неё зависит, платный доступ или льготный" />,
      dataIndex: 'revenue_30d',
      align: 'right',
      sorter: byNumber('revenue_30d'),
      render: formatMoney,
    },
    {
      title: <HintTitle title="Активные ученики" hint="Активных пользователей школы за 30 дней — от них зависит стоимость" />,
      dataIndex: 'active_users',
      align: 'right',
      sorter: byNumber('active_users'),
      render: formatNumber,
    },
    {
      title: <HintTitle title="Последний вход" hint="Последний вход пользователей школы в кабинет" />,
      dataIndex: 'last_activity',
      sorter: byDate('last_activity'),
      render: formatDate,
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className={styles.filters}>
        <Input.Search
          allowClear
          placeholder="Название, домен или id"
          style={{ width: 260 }}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select<TCrmSchoolStatus[]>
          mode="multiple"
          allowClear
          placeholder="Статус"
          style={{ minWidth: 220 }}
          value={statuses}
          onChange={setStatuses}
          options={STATUS_FILTER.map((status) => ({ label: STATUS_META[status].label, value: status }))}
          maxTagCount="responsive"
        />
        <Select<TCrmSignal[]>
          mode="multiple"
          allowClear
          placeholder="Сигналы"
          style={{ minWidth: 220 }}
          value={signals}
          onChange={onSignalsChange}
          options={(Object.keys(SIGNAL_META) as TCrmSignal[]).map((signal) => ({ label: SIGNAL_META[signal].label, value: signal }))}
          maxTagCount="responsive"
        />
        <Checkbox checked={paidOnly} onChange={(e) => setPaidOnly(e.target.checked)}>
          Только платившие
        </Checkbox>
        <span className={styles.muted}>
          Показано {formatNumber(rows.length)} из {formatNumber(schools?.rows.length ?? 0)}
        </span>
      </div>

      <Table<TCrmSchoolRow>
        className={styles.table}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [ '20', '50', '100', '500' ] }}
        scroll={{ x: 'max-content' }}
      />
    </Spin>
  );
};

export default SchoolsTab;
